/* Midnight translation bridge reader (layout/theme.liquid #mw-strings).
   Falls back to the English argument if the bridge is unavailable. Defined
   defensively because several theme scripts share it. */
window.mwString = window.mwString || function (key, fallback) {
  return window.mwT ? window.mwT(key, fallback) : fallback;
};
window.mwCount = window.mwCount || function (key, count, fallbackOne, fallbackOther) {
  var form = count === 1 ? fallbackOne : (fallbackOther === undefined ? fallbackOne : fallbackOther);
  return String(form === undefined ? '' : form).replace('{{ count }}', count);
};
(() => {
  'use strict';

  const ROOT_SELECTOR = '[data-mw-collection-discovery]';
  const PHONE_QUERY = '(max-width: 749px)';
  const PRICE_INPUT_SELECTOR = '#FacetFiltersFormMobile price-range input[type="number"]';

  const parseCount = (value) => {
    const match = String(value || '').match(/\d[\d\s.,\u00a0]*/);
    if (!match) return null;
    const parsed = Number.parseInt(match[0].replace(/\D/g, ''), 10);
    return Number.isFinite(parsed) ? parsed : null;
  };

  /* Currency presentation follows the ACTIVE STOREFRONT LOCALE, which Shopify
     renders into #mw-strings (request.locale.iso_code) and onto <html lang>.
     Never the browser UI language, never a hard-coded locale. The amount, the
     currency and every filter threshold are unchanged - this is presentation
     only. EN still reads \u20ac12,500; pt-PT reads 12 500 \u20ac. */
  const mwLocale = () =>
    (window.mwStrings && window.mwStrings.locale) || document.documentElement.lang || undefined;

  const formatEuro = (value) => {
    const n = Number(value);
    if (!Number.isFinite(n)) return String(value);
    try {
      return new Intl.NumberFormat(mwLocale(), {
        style: 'currency',
        currency: 'EUR',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(n);
    } catch (e) {
      return `\u20ac${n.toLocaleString('en')}`;
    }
  };

  class MidnightCollectionDiscovery {
    constructor(root) {
      if (root.dataset.mwCollectionDiscoveryReady === 'true') return;

      this.root = root;
      this.lastFilterTrigger = null;
      this.wasFilterOpen = false;
      this.sortWasPhone = false;
      this.phoneMedia = window.matchMedia(PHONE_QUERY);
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.syncFrame = null;

      this.onClick = this.onClick.bind(this);
      this.onCaptureClick = this.onCaptureClick.bind(this);
      this.onCaptureInput = this.onCaptureInput.bind(this);
      this.onChange = this.onChange.bind(this);
      this.onKeydown = this.onKeydown.bind(this);
      this.onCancel = this.onCancel.bind(this);
      this.onDocumentPointerDown = this.onDocumentPointerDown.bind(this);
      this.onDocumentKeydown = this.onDocumentKeydown.bind(this);
      this.onBreakpointChange = this.onBreakpointChange.bind(this);
      this.scheduleSync = this.scheduleSync.bind(this);

      root.dataset.mwCollectionDiscoveryReady = 'true';
      root.addEventListener('click', this.onCaptureClick, true);
      root.addEventListener('input', this.onCaptureInput, true);
      root.addEventListener('click', this.onClick);
      root.addEventListener('change', this.onChange);
      root.addEventListener('keydown', this.onKeydown);
      root.addEventListener('cancel', this.onCancel, true);
      document.addEventListener('pointerdown', this.onDocumentPointerDown, true);
      document.addEventListener('keydown', this.onDocumentKeydown);
      if (typeof this.phoneMedia.addEventListener === 'function') {
        this.phoneMedia.addEventListener('change', this.onBreakpointChange);
      }

      this.observer = new MutationObserver(this.scheduleSync);
      this.observer.observe(root, {
        attributes: true,
        attributeFilter: ['class', 'open'],
        childList: true,
        characterData: true,
        subtree: true,
      });

      const pair = this.priceInputs();
      this.lastPriceCommit = pair ? `${pair.gte.value}|${pair.lte.value}` : null;
      this.sync();
    }

    within(target, selector) {
      const node = target.closest(selector);
      return node && this.root.contains(node) ? node : null;
    }

    onCaptureClick(event) {
      // Price bands live inside Dawn's menu-drawer, whose button listener would
      // otherwise close the group. Handle them first and stop there.
      const band = this.within(event.target, '[data-mw-filter-drawer] [data-mw-price-band]');
      if (band) {
        event.preventDefault();
        event.stopImmediatePropagation();
        const pressed = band.getAttribute('aria-pressed') === 'true';
        this.setPrice(pressed ? '' : band.dataset.mwGte || '', pressed ? '' : band.dataset.mwLte || '');
        return;
      }

      const summary = this.within(event.target, '[data-mw-filter-drawer] .mobile-facets__details > summary');
      if (!summary) return;

      event.preventDefault();
      event.stopImmediatePropagation();

      const details = summary.parentElement;
      const willOpen = !details.open;
      details.open = willOpen;
      summary.setAttribute('aria-expanded', String(willOpen));
    }

    onCaptureInput(event) {
      const input = event.target;
      if (!input.matches || !input.matches(PRICE_INPUT_SELECTOR)) return;
      if (input.dataset.mwCommit === 'true') {
        delete input.dataset.mwCommit;
        return;
      }
      // Typing is held back from the facet engine; the range is applied on
      // change (blur or Enter), so a half-typed amount never becomes a filter.
      event.stopPropagation();
      this.scheduleSync();
    }

    onChange(event) {
      const input = event.target;
      if (input.matches && input.matches(PRICE_INPUT_SELECTOR)) this.commitPriceInput(input);
    }

    onClick(event) {
      const { target } = event;

      if (this.within(target, '[data-mw-quick-price]')) {
        if (this.setPrice('', '')) event.preventDefault();
        return;
      }

      const quickFilter = this.within(target, '[data-mw-quick-filter]');
      if (quickFilter) {
        // Falls through to the plain href when the facet input is missing
        // (no JS engine bound, stale markup), so the chip always works.
        if (this.applyQuickFilter(quickFilter)) event.preventDefault();
        return;
      }

      const filterTrigger = this.within(target, '[data-mw-filter-trigger]');
      if (filterTrigger) {
        event.preventDefault();
        this.openFilter(filterTrigger);
        return;
      }

      if (this.within(target, '[data-mw-filter-close]')) {
        event.preventDefault();
        this.closeFilter();
        return;
      }

      const sortOption = this.within(target, '[data-mw-sort-option]');
      if (sortOption) {
        event.preventDefault();
        this.commitSort(sortOption.dataset.mwSortOption);
        return;
      }

      if (this.within(target, '[data-mw-sort-trigger]')) {
        event.preventDefault();
        this.toggleSort();
        return;
      }

      if (this.within(target, '[data-mw-sort-close]')) {
        event.preventDefault();
        this.closeSort();
        return;
      }

      const dialog = this.within(target, '[data-mw-sort-dialog]');
      if (dialog && target === dialog) this.closeSort();
    }

    onKeydown(event) {
      if (!event.target.closest) return;

      const option = this.within(event.target, '[data-mw-sort-option]');
      if (option) {
        const options = this.sortOptions();
        const index = options.indexOf(option);
        let next = null;
        if (event.key === 'ArrowDown') next = options[(index + 1) % options.length];
        else if (event.key === 'ArrowUp') next = options[(index - 1 + options.length) % options.length];
        else if (event.key === 'Home') next = options[0];
        else if (event.key === 'End') next = options[options.length - 1];
        else if (event.key === 'Tab' && !this.sortWasPhone) {
          event.preventDefault();
          this.closeSort({ restoreFocus: false });
          const destination = event.shiftKey
            ? this.sortTrigger()
            : this.root.querySelector('[data-mw-filter-trigger]') || this.sortTrigger();
          destination?.focus({ preventScroll: true });
          return;
        }
        if (next) {
          event.preventDefault();
          next.focus({ preventScroll: true });
        }
        return;
      }

      const trigger = this.within(event.target, '[data-mw-sort-trigger]');
      if (trigger && (event.key === 'ArrowDown' || event.key === 'ArrowUp')) {
        event.preventDefault();
        this.openSort();
      }
    }

    onCancel(event) {
      const dialog = event.target.closest('[data-mw-sort-dialog]');
      if (!dialog || !this.root.contains(dialog)) return;
      event.preventDefault();
      this.closeSort();
    }

    onDocumentPointerDown(event) {
      const dialog = this.sortDialog();
      if (!dialog?.open || this.sortWasPhone) return;
      if (dialog.contains(event.target) || this.sortTrigger()?.contains(event.target)) return;
      this.closeSort({ restoreFocus: false });
    }

    onDocumentKeydown(event) {
      if (event.key !== 'Escape' || !this.sortDialog()?.open) return;
      event.preventDefault();
      this.closeSort();
    }

    onBreakpointChange() {
      if (this.sortDialog()?.open) this.closeSort({ restoreFocus: false });
    }

    // ----- Sort -----------------------------------------------------------

    sortDialog() {
      return this.root.querySelector('[data-mw-sort-dialog]');
    }

    sortTrigger() {
      return this.root.querySelector('[data-mw-sort-trigger]');
    }

    sortOptions() {
      return Array.from(this.root.querySelectorAll('[data-mw-sort-option]'));
    }

    currentSort() {
      const source = this.root.querySelector('#SortBy-mobile') || this.root.querySelector('#SortBy');
      return source ? source.value : '';
    }

    toggleSort() {
      const dialog = this.sortDialog();
      if (!dialog) return;
      if (dialog.open) this.closeSort();
      else this.openSort();
    }

    openSort() {
      const dialog = this.sortDialog();
      const trigger = this.sortTrigger();
      if (!dialog || !trigger || dialog.open) return;

      this.markSort(this.currentSort());
      this.sortWasPhone = this.phoneMedia.matches;

      if (this.sortWasPhone && typeof dialog.showModal === 'function') {
        dialog.showModal();
        document.body.classList.add('overflow-hidden-collection');
      } else if (typeof dialog.show === 'function') {
        dialog.show();
      } else {
        dialog.setAttribute('open', '');
      }

      trigger.setAttribute('aria-expanded', 'true');
      if (!this.sortWasPhone) this.keepSortInView(dialog);

      const options = this.sortOptions();
      const current = options.find((option) => option.getAttribute('aria-checked') === 'true') || options[0];
      window.requestAnimationFrame(() => current?.focus({ preventScroll: true }));
    }

    // The menu always hangs below its frame. When the frame sits too low for
    // the menu to fit, the page scrolls; the menu never moves over its trigger.
    keepSortInView(dialog) {
      const overflow = dialog.getBoundingClientRect().bottom - (window.innerHeight - 16);
      if (overflow > 0) {
        window.scrollBy({ top: overflow, behavior: this.reducedMotion.matches ? 'auto' : 'smooth' });
      }
    }

    closeSort({ restoreFocus = true } = {}) {
      const dialog = this.sortDialog();
      if (!dialog || !dialog.open) return;

      if (typeof dialog.close === 'function') dialog.close();
      else dialog.removeAttribute('open');

      if (!this.filterIsOpen()) document.body.classList.remove('overflow-hidden-collection');
      const trigger = this.sortTrigger();
      trigger?.setAttribute('aria-expanded', 'false');
      if (restoreFocus && trigger) {
        window.requestAnimationFrame(() => trigger.focus({ preventScroll: true }));
      }
    }

    commitSort(value) {
      const filterFormSort = this.root.querySelector('#SortBy-mobile');
      const sortFormSort = this.root.querySelector('#SortBy');
      const changed = this.currentSort() !== value;

      this.markSort(value);
      this.closeSort();
      if (!changed) return;

      if (sortFormSort) sortFormSort.value = value;
      const source = filterFormSort || sortFormSort;
      if (!source) return;
      source.value = value;
      // Sent through the filter form, so active filters stay in the request and in the URL.
      source.dispatchEvent(new Event('input', { bubbles: true }));
    }

    markSort(value) {
      let label = '';
      this.sortOptions().forEach((option) => {
        const selected = option.dataset.mwSortOption === value;
        if (option.getAttribute('aria-checked') !== String(selected)) {
          option.setAttribute('aria-checked', String(selected));
        }
        if (selected) label = option.textContent.trim();
      });

      const node = this.root.querySelector('[data-mw-sort-value]');
      if (node && label && node.textContent.trim() !== label) node.textContent = label;
    }

    // ----- Filter drawer --------------------------------------------------

    filterIsOpen() {
      return Boolean(this.root.querySelector('[data-mw-filter-disclosure]')?.open);
    }

    openFilter(trigger) {
      const disclosure = this.root.querySelector('[data-mw-filter-disclosure]');
      const summary = disclosure?.querySelector(':scope > summary');
      if (!disclosure || !summary) return;

      this.closeSort({ restoreFocus: false });
      this.lastFilterTrigger = trigger;
      summary.click();

      window.requestAnimationFrame(() => {
        this.syncFilterState();
        if (!disclosure.open) return;
        disclosure.querySelector('[data-mw-filter-close]')?.focus({ preventScroll: true });
      });
    }

    closeFilter() {
      const disclosure = this.root.querySelector('[data-mw-filter-disclosure]');
      const summary = disclosure?.querySelector(':scope > summary');
      const drawer = disclosure?.closest('[data-mw-filter-drawer]');
      if (!disclosure?.open || !summary) return;

      if (drawer && typeof drawer.closeMenuDrawer === 'function') {
        const focusTarget = this.lastFilterTrigger?.isConnected
          ? this.lastFilterTrigger
          : this.root.querySelector('[data-mw-filter-trigger]');
        drawer.closeMenuDrawer(new Event('click'), focusTarget || summary);
      } else {
        summary.click();
      }

      window.requestAnimationFrame(() => this.syncFilterState());
    }

    syncFilterState() {
      const disclosure = this.root.querySelector('[data-mw-filter-disclosure]');
      const trigger = this.root.querySelector('[data-mw-filter-trigger]');
      const isOpen = Boolean(disclosure?.open);

      if (
        isOpen &&
        document.body.classList.contains('overflow-hidden-collection') &&
        !disclosure.classList.contains('menu-opening')
      ) {
        const drawer = disclosure.closest('[data-mw-filter-drawer]');
        const summary = disclosure.querySelector(':scope > summary');
        if (drawer && summary && typeof drawer.openMenuDrawer === 'function') {
          const active = document.activeElement;
          const restoredFocus =
            active && active !== summary && disclosure.contains(active)
              ? active
              : disclosure.querySelector('[data-mw-filter-close]');
          drawer.openMenuDrawer(summary);
          window.requestAnimationFrame(() => {
            restoredFocus?.focus({ preventScroll: true });
          });
        } else {
          disclosure.classList.add('menu-opening');
        }
      }

      if (!isOpen && !this.sortDialog()?.open) document.body.classList.remove('overflow-hidden-collection');
      if (trigger && trigger.getAttribute('aria-expanded') !== String(isOpen)) {
        trigger.setAttribute('aria-expanded', String(isOpen));
      }

      if (this.wasFilterOpen && !isOpen) {
        const focusTarget = this.lastFilterTrigger?.isConnected ? this.lastFilterTrigger : trigger;
        if (focusTarget) {
          window.requestAnimationFrame(() => focusTarget.focus({ preventScroll: true }));
        }
      }

      this.wasFilterOpen = isOpen;
    }

    // ----- Price ----------------------------------------------------------

    priceInputs() {
      const inputs = this.root.querySelectorAll(PRICE_INPUT_SELECTOR);
      return inputs.length === 2 ? { gte: inputs[0], lte: inputs[1] } : null;
    }

    commitPriceInput(input) {
      const pair = this.priceInputs();
      const signature = pair ? `${pair.gte.value}|${pair.lte.value}` : input.value;
      if (this.lastPriceCommit === signature) return;
      this.lastPriceCommit = signature;
      input.dataset.mwCommit = 'true';
      input.dispatchEvent(new Event('input', { bubbles: true }));
      this.scheduleSync();
    }

    setPrice(gte, lte) {
      const pair = this.priceInputs();
      if (!pair) return false;

      pair.gte.value = gte;
      pair.lte.value = lte;
      const range = pair.gte.closest('price-range');
      if (range && typeof range.setMinAndMaxValues === 'function') range.setMinAndMaxValues();
      // One input event carries both values into a single request.
      this.commitPriceInput(pair.lte);
      return true;
    }

    priceLabel(gte, lte) {
      if (!gte && !lte) return '';
      const band = Array.from(this.root.querySelectorAll('[data-mw-price-band]')).find(
        (node) => (node.dataset.mwGte || '') === gte && (node.dataset.mwLte || '') === lte
      );
      if (band) return band.textContent.trim();
      if (gte && lte) return `${formatEuro(gte)}–${formatEuro(lte)}`;
      return gte
        ? mwString('facets.price_from', 'From {{ price }}').replace('{{ price }}', formatEuro(gte))
        : mwString('facets.price_up_to', 'Up to {{ price }}').replace('{{ price }}', formatEuro(lte));
    }

    // ----- Shared state ---------------------------------------------------

    syncGroups() {
      const pair = this.priceInputs();
      const gte = pair ? pair.gte.value.trim() : '';
      const lte = pair ? pair.lte.value.trim() : '';

      this.root.querySelectorAll('[data-mw-filter-drawer] .mw-facet-group').forEach((group) => {
        const summary = group.querySelector(':scope > summary');
        if (summary && summary.getAttribute('aria-expanded') !== String(group.open)) {
          summary.setAttribute('aria-expanded', String(group.open));
        }

        const meta = group.querySelector('[data-mw-group-meta]');
        if (!meta) return;
        let next = '';
        if (group.querySelector('price-range')) {
          next = this.priceLabel(gte, lte);
        } else {
          const selected = group.querySelectorAll('input[type="checkbox"]:checked').length;
          next = selected ? mwCount('facets.selected_count', selected, '{{ count }} selected') : '';
        }
        if (meta.textContent.trim() !== next) meta.textContent = next;
      });

      this.root.querySelectorAll('[data-mw-price-band]').forEach((band) => {
        const pressed = Boolean(gte || lte) && (band.dataset.mwGte || '') === gte && (band.dataset.mwLte || '') === lte;
        if (band.getAttribute('aria-pressed') !== String(pressed)) band.setAttribute('aria-pressed', String(pressed));
      });
    }

    resultCount() {
      const catalogueCounts = Array.from(this.root.querySelectorAll('[data-mw-catalogue-count]'));

      if (catalogueCounts.length) {
        return catalogueCounts.reduce((sum, node) => sum + (parseCount(node.textContent) || 0), 0);
      }

      const productCount = this.root.querySelector('#ProductCount');
      const rendered = parseCount(productCount?.textContent);
      if (rendered !== null) return rendered;

      const fallback = this.root.querySelector('[data-mw-total-count]');
      return parseCount(fallback?.dataset.mwTotalCount) || 0;
    }

    updateResultLabels() {
      const count = this.resultCount();
      const noun = mwCount('facets.result_noun', count, 'watch', 'watches');

      this.root.querySelectorAll('[data-mw-toolbar-count]').forEach((node) => {
        const next = mwCount('facets.result_count', count, '{{ count }} watch', '{{ count }} watches');
        if (node.textContent.trim() !== next) node.textContent = next;
      });

      this.root.querySelectorAll('[data-mw-results-cta-count]').forEach((node) => {
        const next = String(count);
        if (node.textContent.trim() !== next) node.textContent = next;
      });

      this.root.querySelectorAll('[data-mw-results-cta-noun]').forEach((node) => {
        if (node.textContent.trim() !== noun) node.textContent = noun;
      });
    }

    activeFilterCount() {
      const details = Array.from(
        this.root.querySelectorAll('#FacetFiltersFormMobile .mobile-facets__details.js-filter')
      );

      return details.reduce((count, detail) => {
        const checked = detail.querySelectorAll('input[type="checkbox"]:checked').length;
        const price = Array.from(detail.querySelectorAll('input[type="number"]')).some(
          (input) => input.value !== ''
        );
        return count + checked + (price ? 1 : 0);
      }, 0);
    }

    updateFilterValue() {
      const count = this.activeFilterCount();
      const node = this.root.querySelector('[data-mw-filter-value]');
      const next = count
        ? mwCount('facets.selected_count', count, '{{ count }} selected')
        : mwString('facets.all', 'All');
      if (node && node.textContent.trim() !== next) node.textContent = next;

      this.root.querySelectorAll('[data-mw-filter-drawer] .mobile-facets__clear').forEach((link) => {
        link.classList.toggle('is-inactive', count === 0);
      });
    }

    quickFilterInput(trigger) {
      const name = trigger.dataset.mwQuickName;
      const value = trigger.dataset.mwQuickValue;
      if (!name || !value || typeof CSS === 'undefined' || !CSS.escape) return null;

      return this.root.querySelector(
        `#FacetFiltersFormMobile input[type="checkbox"][name="${CSS.escape(name)}"][value="${CSS.escape(value)}"]`
      );
    }

    applyQuickFilter(trigger) {
      const input = this.quickFilterInput(trigger);
      if (!input || input.disabled) return false;

      // Drive the real Shopify facet input so filtering, counts, URL state and
      // history keep running through the engine that already owns them.
      input.checked = !input.checked;
      trigger.classList.toggle('is-active', input.checked);
      trigger.setAttribute('aria-pressed', String(input.checked));
      input.dispatchEvent(new Event('input', { bubbles: true }));
      return true;
    }

    syncQuickFilters() {
      this.root.querySelectorAll('[data-mw-quick-filter]').forEach((trigger) => {
        const input = this.quickFilterInput(trigger);
        if (!input) return;

        const active = input.checked;
        trigger.classList.toggle('is-active', active);
        if (trigger.getAttribute('aria-pressed') !== String(active)) {
          trigger.setAttribute('aria-pressed', String(active));
        }
      });
    }

    scheduleSync() {
      if (this.syncFrame) return;
      this.syncFrame = window.requestAnimationFrame(() => {
        this.syncFrame = null;
        this.sync();
      });
    }

    sync() {
      this.syncFilterState();
      this.markSort(this.currentSort());
      this.syncQuickFilters();
      this.syncGroups();
      this.updateResultLabels();
      this.updateFilterValue();
    }
  }

  const initialize = (scope = document) => {
    scope.querySelectorAll(ROOT_SELECTOR).forEach((root) => new MidnightCollectionDiscovery(root));
  };

  initialize();

  document.addEventListener('shopify:section:load', (event) => {
    initialize(event.target);
  });
})();
